'use client';

import { useContext, useEffect } from 'react';
import { GlobalDialogPollingContext } from '../providers/GlobalDialogPollingProvider';

/**
 * Hook to access global dialog polling data.
 *
 * Each dialog should:
 * 1. Call this hook to get the polling data
 * 2. Call `registerDialog(uniqueId, isOpen)` whenever its open state changes
 * 3. Check if the `messageType` matches its expected type
 *
 * @param dialogId - Unique identifier for the dialog (e.g., 'AFFORDABILITY_MIC_POPUP')
 * @param isOpen - Whether the dialog is currently open
 * @returns The current polling data
 *
 * @example
 * ```tsx
 * function MyDialog() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const { data } = useGlobalDialogPolling('MY_DIALOG', isOpen);
 *
 *   useEffect(() => {
 *     if (data?.messageType === 'MY_DIALOG' && !isOpen) {
 *       setIsOpen(true);
 *     }
 *   }, [data?.messageType, isOpen]);
 * }
 * ```
 */
function useGlobalDialogPolling(dialogId: string, isOpen: boolean) {
  const context = useContext(GlobalDialogPollingContext);

  if (!context) {
    throw new Error('useGlobalDialogPolling must be used within GlobalDialogPollingProvider');
  }

  const { registerDialog, data } = context;

  // Register this dialog's open state whenever it changes
  useEffect(() => {
    registerDialog(dialogId, isOpen);
  }, [registerDialog, dialogId, isOpen]);

  return { data };
}

export { useGlobalDialogPolling };
